/**
 * Reports API calls — 6 reports + CSV/PDF exports + AI summary + forecasting.
 */

import client from './client';

// --- Report data endpoints ---

export async function fetchJobCompletionReport(params = {}) {
  const { data } = await client.get('/reports/job-completion/', { params });
  return data.data || data;
}

export async function fetchVendorPerformanceReport(params = {}) {
  const { data } = await client.get('/reports/vendor-performance/', { params });
  return data.data || data;
}

export async function fetchPartPricingHistoryReport(params = {}) {
  const { data } = await client.get('/reports/part-pricing-history/', { params });
  return data.data || data;
}

export async function fetchPnlSummaryReport(params = {}) {
  const { data } = await client.get('/reports/pnl-summary/', { params });
  return data.data || data;
}

export async function fetchPnlDetailReport(params = {}) {
  const { data } = await client.get('/reports/pnl-detail/', { params });
  return data.data || data;
}

export async function fetchPnlAiSummary(params = {}) {
  const { data } = await client.post('/reports/pnl-summary/ai-summary/', params);
  return data;
}

/**
 * Fetch AI-powered backlog forecast with completion estimates and monthly projections.
 * POST /reports/pnl/forecast/
 */
export async function fetchBacklogForecast() {
  const { data } = await client.post('/reports/pnl/forecast/', {});
  return data;
}

export async function fetchLaborUtilizationReport(params = {}) {
  const { data } = await client.get('/reports/labor-utilization/', { params });
  return data.data || data;
}

export async function fetchOpenJobsAgingReport(params = {}) {
  const { data } = await client.get('/reports/open-jobs-aging/', { params });
  return data.data || data;
}

// --- Export helpers ---

async function downloadBlob(url, filename, params = {}) {
  const { data } = await client.get(url, { params, responseType: 'blob' });
  const blobUrl = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

async function downloadBlobPost(url, filename, body = {}) {
  const { data } = await client.post(url, body, { responseType: 'blob' });
  const blobUrl = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

export function exportJobCompletionCSV(params) {
  return downloadBlob('/reports/job-completion/export-csv/', 'job_completion.csv', params);
}
export function exportJobCompletionPDF(params) {
  return downloadBlob('/reports/job-completion/export-pdf/', 'job_completion.pdf', params);
}

export function exportVendorPerformanceCSV(params) {
  return downloadBlob('/reports/vendor-performance/export-csv/', 'vendor_performance.csv', params);
}
export function exportVendorPerformancePDF(params) {
  return downloadBlob('/reports/vendor-performance/export-pdf/', 'vendor_performance.pdf', params);
}

export function exportPartPricingHistoryCSV(params) {
  return downloadBlob('/reports/part-pricing-history/export-csv/', 'part_pricing_history.csv', params);
}
export function exportPartPricingHistoryPDF(params) {
  return downloadBlob('/reports/part-pricing-history/export-pdf/', 'part_pricing_history.pdf', params);
}

export function exportPnlSummaryCSV(params) {
  return downloadBlob('/reports/pnl-summary/export-csv/', 'pnl_summary.csv', params);
}
export function exportPnlSummaryPDF(params) {
  return downloadBlob('/reports/pnl-summary/export-pdf/', 'pnl_summary.pdf', params);
}

export function exportPnlDetailCSV(params) {
  return downloadBlob('/reports/pnl-detail/export-csv/', 'pnl_detail.csv', params);
}
export function exportPnlDetailPDF(params) {
  return downloadBlob('/reports/pnl-detail/export-pdf/', 'pnl_detail.pdf', params);
}

/**
 * Export full P&L PDF with chart, data table, backlog, and AI summary.
 * POST endpoint - sends view mode, filters, and AI summary text.
 */
export async function exportPnlFullPdf(body) {
  const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return downloadBlobPost('/reports/pnl/export-full-pdf/', `pump-source-pnl-${timestamp}.pdf`, body);
}

export function exportLaborUtilizationCSV(params) {
  return downloadBlob('/reports/labor-utilization/export-csv/', 'labor_utilization.csv', params);
}
export function exportLaborUtilizationPDF(params) {
  return downloadBlob('/reports/labor-utilization/export-pdf/', 'labor_utilization.pdf', params);
}

export function exportOpenJobsAgingCSV(params) {
  return downloadBlob('/reports/open-jobs-aging/export-csv/', 'open_jobs_aging.csv', params);
}
export function exportOpenJobsAgingPDF(params) {
  return downloadBlob('/reports/open-jobs-aging/export-pdf/', 'open_jobs_aging.pdf', params);
}
